import styles from './experience-signals.module.scss';
import React, { FC, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ExperienceInsightView } from '../../../models/ai-signals/experience-insights';

interface ExperienceSignalsHeaderProps {
	views: ExperienceInsightView[];
	activeViewType: string | null;
	selectedDate: string;
	onViewChange: React.Dispatch<React.SetStateAction<string | null>>;
	onDateChange: React.Dispatch<React.SetStateAction<string>>;
}

const ExperienceSignalsHeader: FC<ExperienceSignalsHeaderProps> = ({
	views,
	activeViewType,
	selectedDate,
	onViewChange,
	onDateChange,
}) => {
	const dateInputRef = useRef<HTMLInputElement>(null);
	const pillsRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = () => {
		const el = pillsRef.current;
		if (!el) {
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return;
		}
		setCanScrollLeft(el.scrollLeft > 2);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
	};

	useEffect(() => {
		updateScrollState();
		const el = pillsRef.current;
		if (!el) {
			return;
		}
		el.addEventListener('scroll', updateScrollState);
		window.addEventListener('resize', updateScrollState);
		return () => {
			el.removeEventListener('scroll', updateScrollState);
			window.removeEventListener('resize', updateScrollState);
		};
	}, [views]);

	const scrollPillsBy = (offset: number) => {
		const el = pillsRef.current;
		if (!el) {
			return;
		}
		el.scrollBy({ left: offset, behavior: 'smooth' });
	};

	const openDatePicker = () => {
		const input = dateInputRef.current;
		if (!input) {
			return;
		}
		if (typeof input.showPicker === 'function') {
			try {
				input.showPicker();
				return;
			} catch {
			}
		}
		input.focus();
		input.click();
	};

	return (
		<div className={styles.header_bar}>
			<div className={styles.pills_row}>
				{canScrollLeft ? (
					<button
						type="button"
						className={styles.pills_scroll_btn}
						aria-label="Scroll headers left"
						onClick={() => scrollPillsBy(-160)}
					>
						<ChevronLeft size={14} strokeWidth={2.25} />
					</button>
				) : null}
				<div className={styles.pills} ref={pillsRef}>
					{views.map((view) => {
						const isActive = view.viewType === activeViewType;
						return (
							<button
								key={view.viewType}
								type="button"
								className={`${styles.pill}${isActive ? ` ${styles.pill_on}` : ''}`}
								onClick={() => onViewChange(view.viewType)}
							>
								{view.displayName}
							</button>
						);
					})}
				</div>
				{canScrollRight ? (
					<button
						type="button"
						className={styles.pills_scroll_btn}
						aria-label="Scroll headers right"
						onClick={() => scrollPillsBy(160)}
					>
						<ChevronRight size={14} strokeWidth={2.25} />
					</button>
				) : null}
			</div>
			<div className={styles.date_wrap}>
				<div className={styles.date_pill} onClick={openDatePicker}>
					<span className={styles.date_icon} aria-hidden>
						<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
							<rect x="1.5" y="2.5" width="13" height="11.5" rx="2" />
							<path d="M1.5 6.5h13" />
							<path d="M5 1v3M11 1v3" />
						</svg>
					</span>
					<input
						ref={dateInputRef}
						type="date"
						className={styles.date_input}
						value={selectedDate}
						aria-label="Report date"
						onChange={(event) => onDateChange(event.target.value)}
						onClick={(event) => {
							event.stopPropagation();
							openDatePicker();
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default ExperienceSignalsHeader;
